import type { Paragraph, Table } from 'docx';
import type { NodeEntry } from 'slate';

import type { DocumentContext } from './document-context';
import { createDocxParagraph, createDocxTable, createDynamicBlockChildren } from './internal';
import { createToc } from './toc';
import type { ChangeAttributeProperties, DocxOptionsWithTableSuggestions } from './types';
import type { AlineContentBlock, BaseEdit, SuggestionSource } from '../../../slate-api';
import { isTOCElement } from '../../../slate-api';
import { assertNeverThrow } from '../../../util';

export function createDocxAlineContentBlock(
  childEntry: NodeEntry<AlineContentBlock>,
  options: DocxOptionsWithTableSuggestions
): Paragraph | Table | (Paragraph | Table)[] {
  const [child, childPath] = childEntry;

  switch (child.type) {
    case 'paragraph':
      return createDocxParagraph([child, childPath], options);
    case 'table':
      return createDocxTable([child, childPath], options);
    case 'dynamic-block':
      return createDynamicBlockChildren([child, childPath], options);
    case 'instructionField': {
      if (isTOCElement(child)) {
        return createToc(child, options);
      }
      assertNeverThrow(
        child,
        `Unsupported instruction field type in DOCX: ${(child as { type: string }).type}`
      );
      break;
    }
    default:
      assertNeverThrow(child, `Unknown content block type: ${(child as { type: string }).type}`);
  }
}

export function getEditDate(edit: Pick<BaseEdit, 'createdAt' | 'updatedAt'>): string {
  return edit.updatedAt ?? edit.createdAt ?? new Date().toISOString();
}

export function createChangeAttributes(
  context: DocumentContext,
  edit: BaseEdit & { source?: SuggestionSource }
): ChangeAttributeProperties {
  return {
    author: context.getAuthorName(edit.authorId),
    date: getEditDate(edit),
    id: context.generateEditId(edit.groupId ?? edit.source?.groupId),
  };
}
